import React, { useEffect, useState } from 'react';
import { Modal, Select, Input, Form, Tag, Spin, Alert, Progress, message } from 'antd';
import { UsergroupAddOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { affectationService, sansComptesEditeur } from '../../../services/affectationService';
import { CHARTE_COLORS as C } from '../../../variables/constants';
import { filtreSansAccents } from '../../../utils/rechercheTexte';

const { TextArea } = Input;

/**
 * Affecter plusieurs dossiers d'un coup, depuis la liste.
 *
 * Il n'existe pas d'endpoint d'affectation en lot : on boucle sur
 * `PATCH /v1/sinistres/{id}/assigner`, dossier par dossier. C'est volontairement
 * SÉQUENTIEL - chaque appel notifie l'agent et historise l'affectation ; les
 * paralléliser exposerait à des notifications désordonnées pour un gain nul sur
 * les volumes d'une page de liste (25 à 100 lignes).
 *
 * Un échec sur un dossier n'arrête pas les autres : on affiche à la fin ce qui
 * est passé et ce qui ne l'est pas, avec le motif renvoyé par le backend.
 */
export default function AffecterEnMasseModal({ open, onClose, dossiers = [], onAffecte }) {
  const { t } = useTranslation('dossierSinistre');
  const [form] = Form.useForm();
  const [agents, setAgents] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [progression, setProgression] = useState(0);
  const [echecs, setEchecs] = useState([]);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setProgression(0);
    setEchecs([]);
    setChargement(true);
    affectationService.utilisateursAffectables()
      .then((r) => setAgents(sansComptesEditeur(r.data?.data)))
      .catch(() => setAgents([]))
      .finally(() => setChargement(false));
  }, [open, form]);

  const dejaAffectes = dossiers.filter((d) => d.redacteurNomPrenom);

  const valider = async () => {
    const v = await form.validateFields();
    setEnvoi(true);
    setEchecs([]);
    const rates = [];
    let faits = 0;

    for (const d of dossiers) {
      try {
        await affectationService.affecter(d.sinistreTrackingId, {
          redacteurTrackingId: v.redacteurTrackingId,
          consigne: v.consigne?.trim() || null,
        });
      } catch (e) {
        rates.push({
          numero: d.numeroSinistreLocal || d.sinistreTrackingId?.slice(0, 8),
          raison: e?.response?.data?.message || t('affecterEnMasse.erreur'),
        });
      } finally {
        faits += 1;
        setProgression(Math.round((faits / dossiers.length) * 100));
      }
    }

    setEnvoi(false);
    const reussis = dossiers.length - rates.length;
    if (rates.length === 0) {
      message.success(t('affecterEnMasse.reussis', { n: reussis }));
      onAffecte?.();
      onClose();
      return;
    }
    // Des échecs subsistent : on garde la modale ouverte pour les montrer, mais
    // la liste doit quand même refléter les affectations qui ont abouti.
    setEchecs(rates);
    if (reussis > 0) {
      message.warning(t('affecterEnMasse.reussisEtEchecs', { n: reussis, echecs: rates.length }));
      onAffecte?.();
    } else {
      message.error(t('affecterEnMasse.aucun'));
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={valider}
      okText={t('affecterEnMasse.affecterN', { count: dossiers.length })}
      cancelText={t('affectation.annuler')}
      confirmLoading={envoi}
      okButtonProps={{ disabled: dossiers.length === 0 }}
      width={620}
      title={
        <span>
          <UsergroupAddOutlined style={{ color: C.greenMid, marginRight: 8 }} />
          {t('affecterEnMasse.affecterN', { count: dossiers.length })}
        </span>
      }
      destroyOnClose
    >
      {dejaAffectes.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('affecterEnMasse.dejaAffectes', { n: dejaAffectes.length })}
          description={t('affecterEnMasse.dejaAffectesDetail')}
        />
      )}

      {envoi && (
        <Progress percent={progression} status="active" style={{ marginBottom: 16 }} />
      )}

      {echecs.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('affecterEnMasse.echecs', { n: echecs.length })}
          description={
            <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 12.5 }}>
              {echecs.slice(0, 8).map((e, i) => (
                <li key={i}><strong>{e.numero}</strong> - {e.raison}</li>
              ))}
              {echecs.length > 8 && <li>{t('affecterEnMasse.etAutres', { n: echecs.length - 8 })}</li>}
            </ul>
          }
        />
      )}

      <Spin spinning={chargement}>
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            name="redacteurTrackingId"
            label={t('affectation.confierA')}
            rules={[{ required: true, message: t('affectation.choisissezAgent') }]}
          >
            <Select
              showSearch
              filterOption={filtreSansAccents}
              placeholder={t('affectation.rechercherAgent')}
              optionFilterProp="label"
              options={agents.map((a) => ({
                value: a.utilisateurTrackingId,
                // L'e-mail entre dans le libellé de recherche : deux agents peuvent
                // être homonymes, et c'est lui qui les départage.
                label: `${a.nomPrenom} ${a.email || ''}`,
                agent: a,
              }))}
              optionRender={({ data }) => {
                const a = data.agent;
                return (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ minWidth: 0 }}>
                      <span>
                        {a.nomPrenom}
                        {a.profilNom && (
                          <span style={{ color: C.faint, fontSize: 12, marginLeft: 6 }}>{a.profilNom}</span>
                        )}
                      </span>
                      {a.email && (
                        <div style={{
                          fontSize: 11, color: C.faint,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {a.email}
                        </div>
                      )}
                    </span>
                    <span style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <Tag color={a.dossiersEnCours > 15 ? 'red' : a.dossiersEnCours > 8 ? 'orange' : 'green'}
                        style={{ margin: 0, fontSize: 11 }}>
                        {t('affectation.nbDossiers', { count: a.dossiersEnCours })}
                      </Tag>
                    </span>
                  </div>
                );
              }}
            />
          </Form.Item>

          <Form.Item
            name="consigne"
            label={t('affectation.consigneFacultatif')}
            extra={t('affecterEnMasse.consigneAide')}
          >
            <TextArea
              rows={3}
              maxLength={1000}
              showCount
              placeholder={t('affecterEnMasse.consignePlaceholder')}
            />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
}
